import { useMemo } from "react";

import type { DeploymentEvent } from "./useDeploymentEvents";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineRun {
  /** Unique run identifier (maps to sagaId) */
  sagaId: string;
  /** Aggregated status of the entire run */
  status: "completed" | "in_progress" | "failed" | "pending";
  /** When the run started (earliest event timestamp) */
  startedAt: string;
  /** When the run completed (latest event timestamp, or empty) */
  completedAt: string | null;
  /** Duration in seconds (null if still in progress) */
  durationSec: number | null;
  /** Human-readable label for the trigger */
  triggerLabel: string;
  /** Step-level summary */
  steps: { step: string; serviceName: string; status: string }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function runStatusFromSteps(
  steps: { serviceName: string; status: string }[],
): PipelineRun["status"] {
  // Phase-level rows (serviceName === "") always say "completed" and mask
  // per-service failures. Exclude them from status aggregation unless there
  // are no per-service rows (e.g. VCS-only runs).
  const serviceSteps = steps.filter((s) => s.serviceName !== "");
  const check = serviceSteps.length > 0 ? serviceSteps : steps;
  if (check.length === 0) return "pending";
  if (check.some((s) => s.status === "failed")) return "failed";
  if (check.some((s) => s.status === "in_progress")) return "in_progress";
  if (check.every((s) => s.status === "completed")) return "completed";
  return "pending";
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function usePipelineRuns(events: DeploymentEvent[] = []): PipelineRun[] {
  return useMemo(() => {
    if (!events || events.length === 0) return [];

    // Group events by sagaId — skip events missing required fields
    const groups = new Map<string, DeploymentEvent[]>();
    for (const e of events) {
      // Defensive: skip events missing sagaId (malformed WS data)
      if (!e.sagaId) continue;
      const key = e.sagaId;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }

    const runs: PipelineRun[] = [];

    for (const [sagaId, group] of groups) {
      // Sort by createdAt ascending to find start/end
      const sorted = [...group].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );

      // Skip VCS-only runs — VCS provisioning is a one-time prerequisite,
      // not a repeatable pipeline run. A run is VCS-only when every event
      // has step === "vcs" (or has serviceName === "" which is a phase-level row).
      const isVcsOnly = sorted.every(
        (e) =>
          e.step === "vcs" ||
          (e.metadata as Record<string, unknown> | undefined)?.serviceName ===
            "",
      );
      if (isVcsOnly) continue;

      const startedAt = sorted[0].createdAt;
      const lastEvent = sorted[sorted.length - 1];

      const completedAt = sorted.every(
        (e) => e.status === "completed" || e.status === "failed",
      )
        ? lastEvent.createdAt
        : null;

      const startMs = new Date(startedAt).getTime();
      const endMs = completedAt ? new Date(completedAt).getTime() : Date.now();
      const durationSec = completedAt
        ? Math.round((endMs - startMs) / 1000)
        : null;

      // Deduplicate steps by (step, serviceName), keep latest status
      const stepsMap = new Map<
        string,
        { step: string; serviceName: string; status: string }
      >();
      for (const e of sorted) {
        const svcName =
          ((e.metadata as Record<string, unknown> | undefined)
            ?.serviceName as string) ?? "";
        const key = `${e.step}::${svcName}`;
        stepsMap.set(key, {
          step: e.step,
          serviceName: svcName,
          status: e.status,
        });
      }
      const steps = Array.from(stepsMap.values());

      const status = runStatusFromSteps(steps);

      // Derive trigger label from events — make it concise and informative
      const firstEvent = sorted[0];
      const hasBuild = steps.some((s) => s.step === "build");
      const hasDeploy = steps.some((s) => s.step === "deploy");
      const retryCount = sorted.filter(
        (e) => e.eventType?.includes("retry") || e.retryCount > 0,
      ).length;

      // Extract short SHA from message if present
      const shaMatch = firstEvent.message?.match(/([a-f0-9]{7,8})\b/);
      const shortSha = shaMatch ? shaMatch[1] : null;

      let triggerLabel: string;
      if (hasBuild && hasDeploy) {
        triggerLabel = shortSha
          ? `Build + Deploy · ${shortSha}`
          : "Build + Deploy";
      } else if (hasBuild) {
        triggerLabel =
          retryCount > 0
            ? shortSha
              ? `Build retry #${retryCount} · ${shortSha}`
              : `Build retry #${retryCount}`
            : shortSha
              ? `Build · ${shortSha}`
              : "Build";
      } else if (hasDeploy) {
        triggerLabel = shortSha ? `Deploy · ${shortSha}` : "Deploy";
      } else {
        triggerLabel =
          firstEvent.message && firstEvent.message.length < 40
            ? firstEvent.message
            : (firstEvent.eventType?.split(".").pop() ?? "Pipeline");
      }

      runs.push({
        sagaId,
        status,
        startedAt,
        completedAt,
        durationSec,
        triggerLabel,
        steps,
      });
    }

    // Sort by startedAt descending (most recent first)
    runs.sort(
      (a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    );

    return runs;
  }, [events]);
}
